/**
 * reCAPTCHA v3 client helper.
 *
 * Public key baked into the bundle (it's visible in the page source anyway)
 * with an env-var override so prod can ship a different pair without a
 * source change. The companion secret lives in SSM and is used server-side
 * only — see `api/internal/recaptcha`.
 *
 * Usage:
 *
 *   import { executeRecaptcha } from "./recaptcha";
 *   const token = await executeRecaptcha("login");
 *   // send `token` to POST /auth/login as `recaptcha_token`.
 *
 * The script loads lazily on first `executeRecaptcha` call — forms that
 * never submit don't pay the network cost. Repeated calls reuse the same
 * script tag and the same `grecaptcha.ready` promise, so burst submissions
 * (double-click-happy users) don't race.
 */

const DEFAULT_SITE_KEY = "6LfvIcUsAAAAAFxs-UNgOHe-JURQjobNzmpmjAUX";

const SITE_KEY =
  (import.meta.env.VITE_RECAPTCHA_SITE_KEY as string | undefined)?.trim() ||
  DEFAULT_SITE_KEY;

/**
 * The site key is present iff reCAPTCHA should be executed. When empty
 * (intentionally blanked in an env override for tests), callers skip
 * entirely and the token submitted to the API stays empty — matching the
 * server-side "no verifier → no-op" branch.
 */
export function recaptchaEnabled(): boolean {
  return SITE_KEY.length > 0;
}

interface Grecaptcha {
  ready: (cb: () => void) => void;
  execute: (siteKey: string, options: { action: string }) => Promise<string>;
}

declare global {
  interface Window {
    grecaptcha?: Grecaptcha;
  }
}

// Single in-flight loader — subsequent calls await the same promise. The
// script tag is cached in the DOM between route changes (React Router
// doesn't remount <head>), so this is really only called once per page load.
let loaderPromise: Promise<void> | null = null;

function loadScript(): Promise<void> {
  if (loaderPromise) return loaderPromise;

  loaderPromise = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(
      'script[data-recaptcha="v3"]',
    );
    if (existing) {
      if (window.grecaptcha) {
        resolve();
        return;
      }
      existing.addEventListener("load", () => resolve(), { once: true });
      existing.addEventListener("error", () => reject(new Error("recaptcha script failed to load")), { once: true });
      return;
    }

    const s = document.createElement("script");
    s.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(SITE_KEY)}`;
    s.async = true;
    s.defer = true;
    s.dataset.recaptcha = "v3";
    s.addEventListener("load", () => resolve(), { once: true });
    s.addEventListener("error", () => reject(new Error("recaptcha script failed to load")), { once: true });
    document.head.appendChild(s);
  }).catch((err) => {
    // Reset so a retry after a transient network glitch can try again
    // instead of the error getting stuck in the cache forever.
    loaderPromise = null;
    throw err;
  });

  return loaderPromise;
}

/**
 * Executes reCAPTCHA v3 for the given action and resolves to the token.
 *
 * Resolves to an empty string when `recaptchaEnabled()` is false — the
 * caller then sends an empty `recaptcha_token` to the API, which maps to
 * the server-side "verifier disabled" path.
 *
 * Throws when the reCAPTCHA script can't be loaded or `grecaptcha.execute`
 * rejects (rare; usually a network blip). Callers should catch and surface
 * as a generic captcha error.
 */
export async function executeRecaptcha(action: string): Promise<string> {
  if (!recaptchaEnabled()) return "";

  await loadScript();

  // `grecaptcha.ready` runs its callback once the library is fully wired
  // up. We wrap it in a Promise so we can await before calling `execute`.
  await new Promise<void>((resolve) => {
    const poll = () => {
      if (window.grecaptcha?.ready) window.grecaptcha.ready(() => resolve());
      else setTimeout(poll, 30);
    };
    poll();
  });

  if (!window.grecaptcha) throw new Error("recaptcha not available");
  return window.grecaptcha.execute(SITE_KEY, { action });
}
